import makeStyles from "@material-ui/core/styles/makeStyles";
import React, { FC, useEffect, useMemo, useState } from "react";
import { lpResponsiveStyles, SYDM2_SLUG } from "./constants";
import { InView } from "react-intersection-observer";
import { useMediaQuery } from "@material-ui/core";
import useTheme from "@material-ui/core/styles/useTheme";
import {
  FrontProjectService,
  Lottery,
} from "../../../../lib/service/client/FrontProjectService";
import { MessageText } from "../../../admin/message/MessageText";
import { countPadStringArray } from "../../../../lib/util";
import { LazyLoadImage } from "react-lazy-load-image-component";

const backgroundAspectRatio = 5.468;
const contentAspectRatio = backgroundAspectRatio * 0.48;
const useStyles = makeStyles((theme) => {
  const backgroundResponsiveStyle = lpResponsiveStyles(theme, (baseWidth) => ({
    height: `calc(${baseWidth} * ${backgroundAspectRatio})`,
  }));
  const contentRoot = lpResponsiveStyles(theme, (baseWidth) => ({
    width: baseWidth,
    height: `calc(${baseWidth} * ${contentAspectRatio})`,
    top: `calc(${baseWidth} * ${backgroundAspectRatio} * 0.52)`,
  }));
  const twitterFlowLabel = lpResponsiveStyles(theme, (baseWidth) => ({
    position: "absolute",
    textAlign: "center",
    width: baseWidth,
    top: `calc(${baseWidth} * ${contentAspectRatio} * 0)`,
  }));
  const twitterFollowButton = lpResponsiveStyles(theme, (baseWidth) => ({
    position: "absolute",
    textAlign: "center",
    width: baseWidth,
    top: `calc(${baseWidth} * ${contentAspectRatio} * 0.04)`,
  }));
  const whiteBoard = lpResponsiveStyles(theme, (baseWidth) => ({
    position: "absolute",
    textAlign: "center",
    width: baseWidth,
    top: `calc(${baseWidth} * ${contentAspectRatio} * 0.14)`,
  }));
  const whiteBoardImage = lpResponsiveStyles(theme, (baseWidth) => ({
    position: "absolute",
    top: `calc(${baseWidth} * ${contentAspectRatio} * 0.155)`,
    left: `calc(${baseWidth} * 0.5)`,
    right: `calc(${baseWidth} * 0.1)`,
  }));
  const whiteBoardText = lpResponsiveStyles(theme, (baseWidth) => ({
    position: "absolute",
    top: `calc(${baseWidth} * ${contentAspectRatio} * 0.245)`,
    fontSize: `calc(${baseWidth} * ${backgroundAspectRatio} * 0.01)`,
    width: baseWidth,
    lineHeight: "1.4",
    textAlign: "center",
    // padding: `0 10%`
  }));
  const voteResponsive = lpResponsiveStyles(theme, (baseWidth) => ({
    position: "absolute",
    top: `calc(${baseWidth} * ${contentAspectRatio} * 0.38)`,
    // padding: `0 calc(${baseWidth} * 0.15)`,
    width: baseWidth,
    "& div": {
      padding: `calc(${baseWidth} * 0.032) calc(${baseWidth} * 0.06)`,
      margin: `0 calc(${baseWidth} * 0.04)`,
      backgroundSize: "100% 100%",
      backgroundRepeat: "no-repeat",
      fontSize: `calc(${baseWidth} * ${backgroundAspectRatio} * 0.008)`,
      textAlign: "center",
    },
  }));
  const suggestionBoxArea = lpResponsiveStyles(theme, (baseWidth) => ({
    position: "absolute",
    top: `calc(${baseWidth} * ${contentAspectRatio} * 0.5565)`,
    width: baseWidth,
    height: `calc(${baseWidth} * ${contentAspectRatio} * 0.5)`,
    display: "flex",
    justifyContent: "center",
  }));
  const voteCountArea = lpResponsiveStyles(theme, (baseWidth) => ({
    margin: `calc(${baseWidth} * ${contentAspectRatio} * 0.27) 0`,
    // maxWidth: "320px",
  }));
  const voteCountLabel = lpResponsiveStyles(theme, (baseWidth) => ({
    fontSize: `calc(${baseWidth} * 0.04)`,
    color: "white",
    "&.allCount": {
      marginTop: `calc(${baseWidth} * ${contentAspectRatio} * 0.012)`,
    },
  }));
  const voteCount = lpResponsiveStyles(theme, (baseWidth) => ({
    margin: `calc(${baseWidth} * 0.013) 0`,
    padding: `calc(${baseWidth} * 0.013) 0`,
    color: "white",
    "& > span": {
      fontSize: `calc(${baseWidth} * 0.07)`,
      marginRight: `calc(${baseWidth} * 0.013)`,
      padding: `calc(${baseWidth} * 0.013)`,
      backgroundColor: "rgba(255,255,255, 0.5)",
      borderRadius: `calc(${baseWidth} * 0.01)`,
      display: "inline-block",
    },
  }));
  return {
    background: {
      position: "relative",
      height: `calc(100vw * ${backgroundAspectRatio})`,
      backgroundImage: "url(/img/lp/suggestion_box_screen.png)",
      backgroundSize: "100%",
      backgroundRepeat: "no-repeat",
      ...backgroundResponsiveStyle,
    },
    backgroundReverse: {
      position: "relative",
      height: `calc(100vw * ${backgroundAspectRatio})`,
      backgroundImage: "url(/img/lp/suggestion_box_screen_reverse.png)",
      backgroundSize: "100%",
      backgroundRepeat: "no-repeat",
      ...backgroundResponsiveStyle,
    },
    contentRoot: {
      position: "absolute",
      ...contentRoot,
    },
    twitterFlowLabel,
    twitterFollowButton,
    whiteBoard,
    whiteBoardImage,
    whiteBoardText,
    vote: {
      ...voteResponsive,
      padding: "0 20%",
      display: "flex",
      justifyContent: "space-between",
      "& img": {
        width: "24%",
      },
    },
    voteLabel: {
      ...voteResponsive,
      // padding: "0 20%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      writingMode: "vertical-lr",
      // textAlign: "center",
    },
    suggestionBoxAreaObverse: {
      backgroundImage: "url(/img/lp/suggestion_box.png)",
      backgroundSize: "100% auto",
      backgroundRepeat: "no-repeat",
      ...suggestionBoxArea,
    },
    suggestionBoxAreaReverse: {
      backgroundImage: "url(/img/lp/suggestion_box_reverse.png)",
      backgroundSize: "100% auto",
      backgroundRepeat: "no-repeat",
      ...suggestionBoxArea,
    },
    voteCountArea,
    voteCountLabel,
    voteCount,
  };
});

const voteLink = (
  subject: string,
  choice: string,
  tweetImageUrl?: string
): string => {
  const url = new URL("https://twitter.com/intent/tweet");
  const subjectText = `「${subject.replace(/\r?\n/g, "")}」`;
  const choiceText = `→【${
    choice.includes("好きに書いて") ? "   " : choice
  }】で投書しました。`;
  const lpUrl = `https://${process.env.HOST_NAME}/r/lp/${SYDM2_SLUG}`;
  // 画像のURLが一番下にいかないと画像のURLがツイートで
  // 消えないので色々なパラメータをtextに直書きしてる
  const text =
    `＼毎日／生徒会目安箱キャンペーン\n\n✏️今日のお題\n${subjectText}\n${choiceText}\n\n` +
    `＼目指せ絶頂！1919人！／\n「劇場版 生徒会役員共2」応援クラファン\n${lpUrl}\n\n` +
    `#seitokai #SYDクラファン #SYD目安箱\n` +
    (tweetImageUrl || "");
  url.searchParams.append("text", text);
  return url.toString();
};

type Props = {
  contentRoot: Element;
  onView?: () => void;
  onViewContent?: (inView: boolean) => void;
};

export const SpSuggestionBox: FC<Props> = (props: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSp = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <InView
      root={isSp ? null : props.contentRoot}
      rootMargin={"-50% 0px -40% 0px"}
      onChange={(inView, entry) => {
        if (inView) {
          props.onView && props.onView();
        }
      }}
      className={classes.background}
    >
      <div className={classes.contentRoot}>
        <div className={classes.twitterFlowLabel}>
          <LazyLoadImage
            useIntersectionObserver
            src="/img/lp/twitter_follow_label.png"
            width="30%"
          />
        </div>
        <div className={classes.twitterFollowButton}>
          <a href="https://twitter.com/intent/follow?screen_name=animefund">
            <LazyLoadImage
              useIntersectionObserver
              src="/img/lp/twitter_follow_button.png"
              width="70%"
            />
          </a>
        </div>
        <div className={classes.whiteBoard}>
          <LazyLoadImage
            useIntersectionObserver
            src="/img/lp/white_board.png"
            width="100%"
          />
        </div>
        <VoteArea isObverse={true} />
      </div>
    </InView>
  );
};
export const SpSuggestionBoxReverse: FC<Props> = (props: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSp = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <div className={classes.backgroundReverse}>
      <InView
        root={isSp ? null : props.contentRoot}
        rootMargin={"-90% 0px 0px 0px"}
        threshold={0}
        onChange={(inView, entry) => {
          props.onViewContent && props.onViewContent(inView);
        }}
        className={classes.contentRoot}
      >
        <div className={classes.twitterFlowLabel}>
          <LazyLoadImage src="/img/lp/twitter_follow_label.png" width="30%" />
        </div>
        <div className={classes.twitterFollowButton}>
          <a href="https://twitter.com/intent/follow?screen_name=animefund">
            <LazyLoadImage
              useIntersectionObserver
              src="/img/lp/twitter_follow_button.png"
              width="70%"
            />
          </a>
        </div>
        <div className={classes.whiteBoard}>
          <LazyLoadImage
            useIntersectionObserver
            src="/img/lp/white_board.png"
            width="100%"
          />
        </div>
        <VoteArea isObverse={false} />
      </InView>
    </div>
  );
};

type VoteAreaProps = {
  isObverse: boolean;
};
const VoteArea = (props: VoteAreaProps) => {
  const classes = useStyles();
  const service = new FrontProjectService();
  const [lottery, setLottery] = useState<Lottery>();
  const answerCountChars = countPadStringArray(lottery?.answer_count || 0, 8);
  const answerCountAllChars = countPadStringArray(
    lottery?.answer_count_all || 0,
    8
  );
  const maxChoiceLength = useMemo(() => {
    if (lottery?.choice1 && lottery?.choice2 && lottery?.choice3) {
      return Math.max(
        lottery.choice1.length,
        lottery.choice2.length,
        lottery.choice3.length
      );
    } else {
      return 4;
    }
  }, [lottery]);
  useEffect(() => {
    service.getLottery(
      SYDM2_SLUG,
      (lottery) => {
        setLottery(lottery);
      },
      () => {
        setLottery(undefined);
      }
    );
  }, []);
  return (
    <>
      <div className={classes.whiteBoardImage}>
        {lottery?.image_url && lottery?.subject && (
          <LazyLoadImage
            useIntersectionObserver
            src={lottery.image_url}
            alt={lottery.subject}
            width="100%"
          />
        )}
      </div>
      <div className={classes.whiteBoardText}>
        {lottery?.subject ? (
          <MessageText message={lottery.subject} />
        ) : (
          <>準備中</>
        )}
      </div>
      <div className={classes.voteLabel}>
        <Vote
          subject={lottery?.subject}
          choice={lottery?.choice1}
          tweetImageUrl={lottery?.tweet_image_url}
          maxChoiceLength={maxChoiceLength}
          imageUrl={"/img/lp/vote1.png"}
        />
        <Vote
          subject={lottery?.subject}
          choice={lottery?.choice2}
          tweetImageUrl={lottery?.tweet_image_url}
          maxChoiceLength={maxChoiceLength}
          imageUrl={"/img/lp/vote2.png"}
        />
        <Vote
          subject={lottery?.subject}
          choice={lottery?.choice3}
          tweetImageUrl={lottery?.tweet_image_url}
          maxChoiceLength={maxChoiceLength}
          imageUrl={"/img/lp/vote3.png"}
        />
      </div>
      <div
        className={
          props.isObverse
            ? classes.suggestionBoxAreaObverse
            : classes.suggestionBoxAreaReverse
        }
      >
        <div className={classes.voteCountArea}>
          <div className={classes.voteCountLabel}>本日の投書数</div>
          <div className={classes.voteCount}>
            {answerCountChars.map((num) => (
              <span>{num}</span>
            ))}
            人
          </div>
          <div className={`${classes.voteCountLabel} allCount`}>累計投書数</div>
          <div className={classes.voteCount}>
            {answerCountAllChars.map((num) => (
              <span>{num}</span>
            ))}
            人
          </div>
        </div>
      </div>
    </>
  );
};

type VoteProps = {
  subject: string | undefined;
  choice: string | undefined;
  tweetImageUrl: string | undefined;
  maxChoiceLength: number;
  imageUrl: string;
};
const Vote: FC<VoteProps> = (props: VoteProps) => {
  return props.subject && props.choice ? (
    <a
      href={voteLink(props.subject, props.choice, props.tweetImageUrl)}
      target="_blank"
    >
      <div
        style={{
          backgroundImage: `url(${props.imageUrl})`,
          transform: `scale(1 / ${props.maxChoiceLength} * 8)`,
          transformOrigin: "0 0",
        }}
      >
        {props.choice}
      </div>
    </a>
  ) : (
    <div
      style={{
        backgroundImage: `url(${props.imageUrl})`,
        transform: `scale(1 / ${props.maxChoiceLength} * 8)`,
        transformOrigin: "0 0",
      }}
    >
      準備中
    </div>
  );
};
